import { useState } from "react"
import { Navigate, useNavigate, useParams } from "react-router-dom"

import { Breadcrumb, Divider, Flex, Link, Text } from "@/atoms"
import { NAMES_ROUTES, VALUES_PARAMS } from "@/const/routes.constants"
import { Trans, useTranslation } from "@/hooks/useTranslation"
import { AlignLeftOutlined, FileProtectOutlined, StopOutlined } from "@/icons"
import { Heading } from "@/molecules/Heading/Heading"
import { Form } from "@/organisms/Form"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { PageWithBreadcrumbTemplate } from "@/templates/PageWithBreadcrumbTemplate"
import { getDecisionsUrl } from "../AiPoliciesPage/const"
import { AiPolicyForm } from "./components/AiPolicyForm"
import {
  ENFORCEMENT_SECTION_ID,
  EnforcementSection
} from "./components/EnforcementSection"
import { RuleCardList } from "./components/RuleCardList"
import { SummarySection } from "./components/SummarySection"

import styles from "./AiPolicyPage.module.scss"

import type { AiPolicyFormValues } from "./components/AiPolicyForm/AiPolicyForm.types"

export const AiPolicyPage = () => {
  const { t } = useTranslation("translation")
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">(
    "idle"
  )
  const { response, mode, isPending, features } = useAiPolicies()
  const { ROOT, AI } = NAMES_ROUTES.GOVERNANCE
  const listUrl = `/${ROOT}/${AI}`
  const isNew = id === VALUES_PARAMS.NEW_ENTITY
  const record = response?.data?.[0]

  if (record?.type === "heading") return null

  const { Details, Resources } = Form.Section
  const asideItems = [
    { key: Details.sectionId, tKey: "AI Policy Details" },
    ...(features.editableScope
      ? [
          {
            key: Resources.sectionId,
            tKey: "governance.ai.form.resources.title"
          }
        ]
      : []),
    { key: "rules", tKey: "Rules" },
    ...(features.enforcement
      ? [
          {
            key: ENFORCEMENT_SECTION_ID,
            tKey: "governance.ai.live.form.enforcement.title"
          }
        ]
      : [])
  ]
  const headerItems = [
    { link: ROOT, tKey: "navigation.governance" },
    { link: listUrl, tKey: "AI Policy" },
    {
      fieldName: "name_details",
      title: record?.policyName,
      tKey: "Create AI Policy"
    }
  ]
  const rootUrl = record ? `${listUrl}/${record.id}` : listUrl
  const cancelRedirect = record && !isNew ? `${rootUrl}/view` : listUrl

  // A real policy that is gone is not edited as a new one
  if (features.enforcement && !isNew && !isPending && !record)
    return <Navigate replace to={listUrl} />

  if (mode === "edit")
    return (
      <Form.Page
        loading={isPending}
        id="ai-policy-form"
        aside={
          <Form.Aside items={asideItems} followScroll={features.enforcement} />
        }
        header={<Form.Header items={headerItems} />}
        footer={
          <Form.Footer
            submitting={saveState === "saving"}
            cancelRedirect={cancelRedirect}
            okText={(isNewEntity) =>
              isNewEntity
                ? t("governance.ai.form.buttons.apply")
                : t("general.saveUpdate")
            }
            saveGuard={(isNewEntity, values: AiPolicyFormValues) => {
              const name = values.name_details
              const key = isNewEntity ? "created" : "updated"

              return {
                content: (
                  <Trans>
                    {t(`governance.ai.form.messages.guard.${key}`, { name })}
                  </Trans>
                )
              }
            }}
          />
        }
      >
        <AiPolicyForm
          record={record}
          redirectUrl={listUrl}
          setUpdating={(updating) =>
            setSaveState(updating ? "saving" : "saved")
          }
        />
      </Form.Page>
    )

  // A view link to a policy that is not in this session's store falls back
  // to the list instead of an empty page.
  if (!record) return isPending ? null : <Navigate replace to={listUrl} />

  const { policyName, policyDescription } = record

  return (
    <PageWithBreadcrumbTemplate
      full
      loading={isPending}
      header={
        <Breadcrumb
          items={headerItems.map((item) => {
            const { tKey = "", link, title = t(tKey) } = item

            if (link) return { title: <Link to={link}>{title}</Link> }

            return { title: item.title }
          })}
        />
      }
    >
      <Flex vertical>
        <Flex gap={10} align="center" className={styles.sectionTitle}>
          <AlignLeftOutlined className={styles.sectionIcon} />
          <Heading title="AI Policy Details" level={4} />
        </Flex>
        <Divider className={styles.divider16} />
        <SummarySection
          record={record}
          onClick={() => navigate(`${rootUrl}/edit`)}
          onViewDecisions={
            features.enforcement && record.live
              ? () => navigate(getDecisionsUrl(`${record.id}`))
              : undefined
          }
        />
        <Divider className={styles.divider16} />
        <Flex vertical gap={12}>
          <Flex vertical>
            <Text type="secondary" className="sm-text-ks">
              AI Policy Name
            </Text>
            <Text>{policyName}</Text>
          </Flex>
          <Flex vertical>
            <Text type="secondary" className="sm-text-ks">
              Description
            </Text>
            <Text>{policyDescription}</Text>
          </Flex>
        </Flex>
        <Flex gap={10} align="center" className={styles.sectionTitle}>
          <FileProtectOutlined className={styles.sectionIcon} />
          <Heading title="Rules" level={4} />
        </Flex>
        <Divider className={styles.divider16} />
        <RuleCardList record={record} />
        {features.enforcement && record.live && (
          <>
            <Flex
              gap={10}
              align="center"
              className={styles.sectionTitle}
              id={ENFORCEMENT_SECTION_ID}
            >
              <StopOutlined className={styles.sectionIcon} />
              <Heading
                title={t("governance.ai.live.form.enforcement.title")}
                level={4}
              />
            </Flex>
            <Divider className={styles.divider16} />
            <EnforcementSection policy={record.live} />
          </>
        )}
      </Flex>
    </PageWithBreadcrumbTemplate>
  )
}
